import React, { useState, useEffect, useRef } from 'react';
import { Ear, Search, Megaphone } from 'lucide-react';
import { Link } from 'react-router-dom';
import '../styles/AboutUs.css';
import Footer from '../components/Footer';
import UniversalCTA from '../components/UniversalCTA';
import SEO from '../components/SEO';
import LuxeCompassWatermark from '../components/LuxeCompassWatermark';

// PAGE 2: HEART - Mission + Emotion ("Why CADENCE Exists")
// Order: Emblem -> Title -> Quote -> Family Photo -> Peace Quote -> Coin -> Listen Deeply Pillars -> Founders link
// RULE: This page is purely the WHY. Differentiators ("What Makes CADENCE Different?")
// live on /cadence-promise as the pie-chart section. Do NOT duplicate them here.

// Lightweight reveal-on-scroll hook (single-fire, respects prefers-reduced-motion)
const useReveal = (threshold = 0.25) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setShown(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            obs.unobserve(e.target);
          }
        });
      },
      { threshold, rootMargin: '0px 0px -10% 0px' }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [threshold]);

  return [ref, shown];
};

const Heart = () => {
  const [familyRef, familyShown] = useReveal(0.2);
  const [coinRef, coinShown] = useReveal(0.3);

  return (
    <div className="heart-page">
      <SEO
        title="About Us — Two Veterans Who Hear You"
        description="Founded by two military medical veterans, CADENCE exists for patients who've been dismissed, talked over, or left without answers. Learn how we hear you when others won't."
        path="/about-us"
      />
      {/* ORDER (2026-06 founder restructure, revised):
          Top emblem → Title → Mission statement → Crest (coin) →
          Ear/Advocate emblem bar → couple photo → swapped quote →
          Sound Familiar box (last). */}

      {/* 0. TOP EMBLEM — standard gold-foil page emblem */}
      <section className="emblem-section" data-testid="heart-emblem">
        <div className="container">
          <div className="emblem-wrapper">
            <img
              src="/cadence-gold-foil.png"
              alt="CADENCE Medical Consulting"
              className="velvet-emblem-home"
            />
          </div>
        </div>
      </section>

      {/* 1. TITLE - "The CADENCE Mission" */}
      <section className="unified-content-section" data-testid="heart-title">
        <div className="container">
          <LuxeCompassWatermark />
          <h1 className="unified-page-title">The CADENCE Mission</h1>
        </div>
      </section>

      {/* 2. MISSION STATEMENT — styled to match the standard first-quote
          format used at the top of every curtain page (2026-06: dropped
          the "OUR MISSION" eyebrow — the page title already says Mission,
          and the line now uses the same unified-page-quote treatment as
          the opening quote on every other page for cross-page consistency). */}
      <section className="unified-quote-section" data-testid="about-mission">
        <div className="container">
          <blockquote className="unified-page-quote">
            "To translate medicine into clarity &mdash; and stand beside every patient until they have the answers, dignity, and care they&nbsp;deserve."
          </blockquote>
        </div>
      </section>

      {/* 3. CREST — the CADENCE coin / medallion */}
      <section className="heart-coin-section" data-testid="heart-coin">
        <div className="container">
          <div
            ref={coinRef}
            className={`coin-wrapper scroll-reveal scroll-reveal-coin ${coinShown ? 'is-revealed' : ''}`}
          >
            <img
              src="/local-images/about-us-photo2.jpg"
              alt="CADENCE Medical Consulting Medallion"
              className="coin-medallion-heart"
            />
          </div>
        </div>
      </section>

      {/* 4. LISTEN · INVESTIGATE · ADVOCATE — Ear/Advocate emblem bar */}
      <section className="heart-mission-tile-section" data-testid="heart-mission-tile">
        <div className="container">
          {/* Hidden gradient defs — engraved gold treatment */}
          <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
            <defs>
              <linearGradient id="heart-engraved-gold" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#F5D86E" />
                <stop offset="48%" stopColor="#C9A227" />
                <stop offset="100%" stopColor="#5C2E0E" />
              </linearGradient>
            </defs>
          </svg>
          <div className="mission-copper-tile">
            <div className="mission-pillar">
              <span className="mission-pillar-sigil" aria-hidden="true">
                <Ear size={36} strokeWidth={2.25} />
              </span>
              <p className="mission-pillar-line">Listen Deeply</p>
            </div>
            <span className="mission-pillar-divider" aria-hidden="true" />
            <div className="mission-pillar">
              <span className="mission-pillar-sigil" aria-hidden="true">
                <Search size={36} strokeWidth={2.25} />
              </span>
              <p className="mission-pillar-line">Investigate Thoroughly</p>
            </div>
            <span className="mission-pillar-divider" aria-hidden="true" />
            <div className="mission-pillar">
              <span className="mission-pillar-sigil" aria-hidden="true">
                <Megaphone size={36} strokeWidth={2.25} />
              </span>
              <p className="mission-pillar-line">Advocate Fiercely</p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. COUPLE PHOTO */}
      <section className="heart-family-section" data-testid="heart-family">
        <div className="container">
          <div
            ref={familyRef}
            className={`family-photo-wrapper scroll-reveal ${familyShown ? 'is-revealed' : ''}`}
          >
            <img
              src="/local-images/about-us-photo1.png"
              alt="Family supporting patient in healthcare journey"
              className="family-photo-image"
            />
          </div>
        </div>
      </section>

      {/* 6. SWAPPED QUOTE — the old top quote, now below the couple photo */}
      <section className="unified-quote-section" data-testid="heart-free-quote">
        <div className="container">
          <blockquote className="unified-page-quote">
            "Because everyone deserves to be heard, understood, and advocated&nbsp;for."
          </blockquote>
          <p className="quote-attribution">— The Founders</p>
        </div>
      </section>

      {/* 7. EMPATHY HOOK — "Sound Familiar" box, LAST */}
      <section className="second-opinion-section" data-testid="second-opinion-hook">
        <div className="container">
          <div className="second-opinion-tile">
            <h2 className="second-opinion-heading">We Hear You When Others Won&rsquo;t</h2>
            <ul className="second-opinion-list">
              <li>Your labs are &ldquo;normal&rdquo; but you still feel terrible</li>
              <li>You&rsquo;ve seen 3+ specialists and still don&rsquo;t have a clear answer</li>
              <li>Your symptoms were dismissed as stress, anxiety, or aging</li>
              <li>No one has sat down and read through your full medical history</li>
              <li>You leave every appointment with more questions than answers</li>
            </ul>
            <p className="second-opinion-closer">If any of this sounds familiar, you&rsquo;re not imagining it. And you&rsquo;re not&nbsp;alone.</p>
          </div>
        </div>
      </section>

      {/* 10. SWIPE INDICATOR */}
      <section className="swipe-indicator-section" data-testid="swipe-indicator">
        <div className="container">
          <Link
            to="/founders"
            className="swipe-hint-text"
            data-testid="swipe-to-about"
          >
            Swipe to meet our founders →
          </Link>
        </div>
      </section>

      <UniversalCTA />

      <Footer />
    </div>
  );
};

export default Heart;
